import React from "react";
import { useTinyHost } from "@tiny/host-contract";
import { useApi } from "@tiny/api";
import { usePutSegmentBySegmentIdMutation } from "@tiny/api";
import { useMessages } from "@tiny/components";
import useBreakpoints from "@tiny/components/src/hooks/useBreakpoints";
import { useViewSegmentView } from "../ViewSegment.context";

export function useViewSegmentHeader() {
  const api = useApi();
  const { mdUp } = useBreakpoints();

  const { navigate } = useTinyHost();

  const { showWarningMessage, closeMessage, showErrorMessage } = useMessages();

  const {
    segmentId,
    initialSegmentName,
    filter,
    openSaveAs,
    isPredefined,
    editDisabled,
    editDisabledTooltip,
    isLoading: isSegmentLoading,
  } = useViewSegmentView();

  const [nameEditing, setNameEditing] = React.useState(false);

  const [segmentName, setSegmentName] = React.useState("");

  const [summaryOpen, setSummaryOpen] = React.useState(false);

  const messageIdRef = React.useRef<string | undefined>();

  const { mutateAsync: putSegmentMutateAsync, isLoading } =
    usePutSegmentBySegmentIdMutation({
      onError() {
        return undefined;
      },
    });

  const clearMessage = React.useCallback(async () => {
    if (messageIdRef.current) {
      await closeMessage(messageIdRef.current);
      messageIdRef.current = undefined;
    }
  }, [closeMessage]);

  const openSummary = () => {
    setSummaryOpen(true);
  };

  const closeSummary = () => {
    setSummaryOpen(false);
  };

  const editName = () => {
    setNameEditing(true);
  };

  const cancelEditName = () => {
    setNameEditing(false);
  };

  const saveName = async (value: string) => {
    clearMessage();
    if (value && value !== segmentName) {
      try {
        const { data: nameIsUsed } = await api.putSegmentCheckExists({
          name: value,
          type: "CUSTOM",
        });
        if (nameIsUsed) {
          messageIdRef.current = showWarningMessage(
            `Segment with name "${value}" already exists!`
          );
        } else {
          await putSegmentMutateAsync({
            segmentId: segmentId!,
            data: { name: value },
          });
          setSegmentName(value);
          setNameEditing(false);
        }
      } catch (e) {
        showErrorMessage("Something wrong happened");
      }
    } else {
      setNameEditing(false);
    }
  };

  const editSegment = () => {
    navigate.to(`/main/audience/edit-segments/${segmentId}`, {
      state: {
        backUrl: {
          pathname: "/main/audience",
          search: "tab=custom_segments",
        },
      },
    });
  };

  React.useEffect(() => {
    setSegmentName(initialSegmentName || "");
  }, [initialSegmentName]);

  return {
    mdUp,
    nameEditing,
    editName,
    saveName,
    segmentName,
    cancelEditName,
    isLoading,
    isSegmentLoading,
    editSegment,
    summaryOpen,
    openSummary,
    closeSummary,
    filter,
    openSaveAs,
    isPredefined,
    editDisabled,
    editDisabledTooltip,
  };
}
